import { endOfWeek, startOfWeek } from 'date-fns'
import dayjs from 'dayjs'
import * as d3 from 'd3'
import {
  AcaCoverageFilter,
  AnalysisData,
  AnalysisDataTable,
  AnalysisDataV2,
  AnalyzerChartData,
  AnalyzerData,
  AnalyzerDataExtended,
  AnalyzerDataValue,
  AnalyzerDataValueExtended,
  AnalyzerDataValueFilled,
  ClusterRecord,
  CPIFrequency,
  MatchingRecord,
  CPITimePeriod,
  DummyObjectAca,
  IntegrationInstance
} from 'models'
import { dateFormat } from 'lib/utils'
import {
  convertDateToUTCString,
  getUtcDateAndTime,
  generateStringDatesArray,
  getSelectedPeriodNumber,
  getRangeFromToFormatted,
  getTodayTimeUTC,
  fillDatesBetweenRange
} from '../library/utils'

// Shared by the trends tab's initial state and its Reset button, which drifted apart.
export const DEFAULT_TRENDS_TIME_PERIOD = CPITimePeriod.ThreeMonths

export const formatData = (
  data: AnalyzerData,
  firstSource: AnalyzerChartData,
  secondSource: AnalyzerChartData
): AnalyzerDataExtended => {
  const valuesObject: any = {}

  for (const [key, value] of Object.entries(data)) {
    if (key === firstSource.id) {
      valuesObject.firstSource = {
        id: key,
        values: value
      }
    }
    if (key === secondSource.id) {
      valuesObject.secondSource = {
        id: key,
        values: value
      }
    }
  }

  valuesObject.values = {}
  const formattedValues = []

  /* 1, 2 */
  if (valuesObject?.firstSource?.id && valuesObject?.secondSource?.id) {
    valuesObject.firstSource.values.forEach((firstSourceValue: any) => {
      valuesObject.values[firstSourceValue.xValue] = {
        y1Value: firstSourceValue.yValue
      }
    })
    valuesObject.secondSource.values.forEach((secondSourceValue: any) => {
      valuesObject.values[secondSourceValue.xValue] = {
        ...valuesObject.values[secondSourceValue.xValue],
        y2Value: secondSourceValue.yValue
      }
    })

    for (const [key, value] of Object.entries(valuesObject.values)) {
      formattedValues.push({
        xValue: key,
        y1Value: (value as any).y1Value,
        y2Value: (value as any).y2Value
      })
    }
  }
  /* 1, !2 */
  if (valuesObject?.firstSource?.id && !valuesObject?.secondSource?.id) {
    valuesObject.firstSource.values.forEach((firstSourceValue: any) => {
      valuesObject.values[firstSourceValue.xValue] = {
        y1Value: firstSourceValue.yValue
      }
    })

    for (const [key, value] of Object.entries(valuesObject.values)) {
      formattedValues.push({
        xValue: key,
        y1Value: (value as any).y1Value
      })
    }
  }
  /* !1, 2 */
  if (valuesObject?.secondSource?.id && !valuesObject?.firstSource?.id) {
    valuesObject.secondSource.values.forEach((secondSourceValue: any) => {
      valuesObject.values[secondSourceValue.xValue] = {
        y2Value: secondSourceValue.yValue
      }
    })

    for (const [key, value] of Object.entries(valuesObject.values)) {
      formattedValues.push({
        xValue: key,
        y2Value: (value as any).y2Value
      })
    }
  }
  valuesObject.values = formattedValues

  return { values: valuesObject.values }
}

export const buildEmptyData = (selectedTimePeriod?: CPITimePeriod, firstSourceId?: string) => {
  const today = dayjs().format(dateFormat.customLabel)
  const end = today

  // Determine the number of months based on selected time period
  let monthsToSubtract = 3 // default to 3 months
  if (selectedTimePeriod) {
    if (selectedTimePeriod === CPITimePeriod.PastYear) {
      monthsToSubtract = 12
    } else {
      // Parse "3 Months", "6 Months", etc.
      const parsed = parseInt(selectedTimePeriod.split(' ')[0])
      if (!isNaN(parsed)) {
        monthsToSubtract = parsed
      }
    }
  }

  const start = dayjs(today).subtract(monthsToSubtract, 'months')
  const startFormatted = dayjs(start).format(dateFormat.customLabel)
  const data = fillDatesBetweenRange(new Date(startFormatted), new Date(end))
  // Use y1Value for the placeholder data since it's used directly as filteredData
  const dataFormatted = data.map((n) => ({
    xValue: n.slice(0, 10),
    yValue: 0,
    y1Value: 0
  }))

  // Use the actual firstSourceId as the key, or fallback to placeholder
  const sourceKey = firstSourceId || 'emptyDataPlaceholder'

  const d = {
    [sourceKey]: dataFormatted
  }
  const firstSource = {
    id: sourceKey,
    integrationName: sourceKey,
    instanceName: sourceKey,
    status: 'ready',
    values: dataFormatted
  }

  // Second source has no values - will not render a line
  const secondSource = {
    id: 'emptyDataPlaceholder2',
    integrationName: 'emptyDataPlaceholder2',
    instanceName: 'emptyDataPlaceholder2',
    status: 'ready',
    values: [] as { xValue: string; yValue: number }[]
  }

  return {
    data: d,
    months: monthsToSubtract,
    firstSource,
    secondSource
  }
}

export const filterData = (months: number, data: any) => {
  const today = new Date()

  return data.values.filter((item: any) => {
    const itemDate = new Date(item.xValue)
    const utcItemDate = new Date(Date.UTC(itemDate.getUTCFullYear(), itemDate.getUTCMonth(), itemDate.getUTCDate()))
    const utcStartDate = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - months, 1))

    return utcItemDate >= utcStartDate
  })
}

export const findUniqueMonths = (
  filteredData: AnalyzerDataValue[],
  timePeriod: CPITimePeriod,
  data?: AnalyzerDataExtended,
  analysis = false
): string[] => {
  const uniqueMonths: string[] = []

  if (timePeriod === CPITimePeriod.Week) {
    filteredData.forEach((item: any) => {
      const date = new Date(item.xValue)
      const d = getUtcDateAndTime(item.xValue).labelFormatDate
      const year = date.getFullYear()
      const month = date.getMonth() + 1
      const day = date.getDate()
      const monthKey = `${year}-${month.toString().padStart(2, '0')}-${day}`

      if (analysis) {
        if (!uniqueMonths.includes(d)) {
          uniqueMonths.push(d)
          return
        }
      }

      if (!uniqueMonths.includes(monthKey)) {
        uniqueMonths.push(monthKey)
      }
    })
  } else if (timePeriod === CPITimePeriod.Month && data) {
    const grouped = groupByWeeks(data)

    return grouped.values.map((n: any) => n.xValue)
  } else {
    filteredData.forEach((item: any) => {
      const date = new Date(item.xValue)
      const year = date.getFullYear()
      const month = date.getMonth() + 1
      const monthKey = `${year}-${month.toString().padStart(2, '0')}`

      if (!uniqueMonths.includes(monthKey)) {
        uniqueMonths.push(monthKey)
      }
    })
  }

  return uniqueMonths
}

export const groupByWeeks = (data: AnalyzerDataExtended): AnalyzerDataExtended => {
  const groupedData: {
    [key: string]: {
      xValue: string
      y1Value: number
      y2Value: number
      start: string
      end: string
    }
  } = {}

  data.values.forEach((entry) => {
    const date = new Date(entry.xValue)
    const year = date.getFullYear()

    const startOfWeekDate = startOfWeek(date, { weekStartsOn: 1 })
    const startDay = startOfWeekDate.getDate()
    const startMonth = startOfWeekDate.getMonth() + 1

    const endOfWeekDate = endOfWeek(date, { weekStartsOn: 1 })
    const endYear = endOfWeekDate.getFullYear()
    const endDay = endOfWeekDate.getDate()
    const endMonth = endOfWeekDate.getMonth() + 1
    const groupKey = `${year}-W${startMonth < 10 ? '0' : ''}${startMonth}-${startDay < 10 ? '0' : ''}${startDay}`

    if (!groupedData[groupKey]) {
      groupedData[groupKey] = {
        xValue: entry.xValue,
        y1Value: entry.y1Value,
        y2Value: entry.y2Value,
        start: `${year}-${startMonth < 10 ? '0' : ''}${startMonth}-${startDay < 10 ? '0' : ''}${startDay}`,
        end: `${endYear}-${endMonth < 10 ? '0' : ''}${endMonth}-${endDay < 10 ? '0' : ''}${endDay}`
      }
    }
  })

  const result: AnalyzerDataValueExtended[] = []
  for (const key in groupedData) {
    const prunedXValue = key.replace('W', '')
    const xValue = dayjs(prunedXValue).format('YYYY-MM-DD')

    result.push({
      xValue: xValue || '',
      y1Value: groupedData[key].y1Value,
      y2Value: groupedData[key].y2Value,
      start: groupedData[key].start || '',
      end: groupedData[key].end || ''
    })
  }

  return { ...data, values: result }
}

export const filterDateByDay = (data: AnalyzerDataValueExtended[], day: string): AnalyzerDataValueExtended[] => {
  const currentDate = new Date(day).getDate()

  return data.filter((e) => {
    const thisDate = new Date(e.xValue).getDate()

    return thisDate === currentDate
  })
}

export const filterDateByWeek = (data: AnalyzerDataValueExtended[], day: string): AnalyzerDataValueExtended[] => {
  const date = new Date(day)
  const startOfWeekDate = startOfWeek(date, { weekStartsOn: 1 }) // 1 means Monday
  const endOfWeekDate = endOfWeek(date, { weekStartsOn: 1 }) // 1 means Monday
  const startTime = new Date(startOfWeekDate).getTime()
  const endTime = new Date(endOfWeekDate).getTime()

  return data.filter((e) => {
    const currentDayTime = new Date(e.xValue).getTime()

    return currentDayTime >= startTime && currentDayTime < endTime
  })
}

export const filterDateByMonth = (data: AnalyzerDataValueExtended[], month: string): AnalyzerDataValueExtended[] => {
  let d = month
  const curDat = data.filter((n) => n.xValue.includes(month))
  if (!curDat.length) return []
  const currentDate = convertDateToUTCString(new Date(curDat[0].xValue))
  if (currentDate) d = currentDate
  const currentMonth = new Date(d).getMonth()
  const currentYear = new Date(d).getFullYear()

  return data.filter((e) => {
    const thisMonth = new Date(e.xValue).getMonth()
    const thisYear = new Date(e.xValue).getFullYear()

    return thisMonth === currentMonth && thisYear === currentYear
  })
}

export interface D3PositionAnalyzer {
  x: number
  y1: number
  y2: number
  value?: number
  date?: string
  monthIndex?: number
  dummy?: boolean
}

export const getPathAndPositions = (
  uniqueMonths: string[],
  stripeWidth: number,
  height: number,
  filteredData: AnalyzerDataValueExtended[],
  timePeriod: CPITimePeriod,
  yAxisDomain: [number, number],
  isReady: boolean,
  firstSource: AnalyzerChartData,
  secondSource: AnalyzerChartData,
  paddingTop?: number
): [string, string, Array<D3PositionAnalyzer>] => {
  let yellowPathStr = ``
  let bluePathStr = ``
  const positions: D3PositionAnalyzer[] = []
  // Track if we need to start a new line segment (after null values)
  let yellowNeedsMoveTo = true
  let blueNeedsMoveTo = true

  for (let i = 0; i < uniqueMonths.length; i++) {
    const stripeWidthStart = stripeWidth * i
    const stripeWidthEnd = stripeWidth * (i + 1)

    let groupedMonths: AnalyzerDataValueExtended[] = []
    if (timePeriod === CPITimePeriod.Week) {
      const currentDate = getUtcDateAndTime(uniqueMonths[i])
      groupedMonths = filterDateByDay(filteredData, currentDate.utcFormattedDate)
    } else if (timePeriod === CPITimePeriod.Month) {
      groupedMonths = filterDateByWeek(filteredData, uniqueMonths[i])
    } else {
      const month = dayjs(uniqueMonths[i]).format('YYYY-MM')
      groupedMonths = filterDateByMonth(filteredData, month)
    }

    if (!groupedMonths.length) {
      positions.push({
        x: 0,
        y1: 0,
        y2: 0,
        value: 0
      })
      // Don't add to path for empty months, mark that next valid point needs MoveTo
      yellowNeedsMoveTo = true
      blueNeedsMoveTo = true
      continue
    }

    const stripeMiddle = !isReady && i === 0 ? 0 : stripeWidth / (groupedMonths.length * 2)

    const xScaleEach = d3
      .scaleBand()
      .domain(groupedMonths.map((d) => d.xValue))
      .range([stripeWidthStart, stripeWidthEnd])
      .padding(0)
    const yScaleEach = d3
      .scaleLinear()
      .domain(yAxisDomain)
      .nice()
      .range([height, paddingTop ?? 0])

    groupedMonths.forEach((each) => {
      const add = isReady ? stripeMiddle : stripeMiddle * 2
      const xPos = (xScaleEach(each.xValue) || 0) + add

      // Check if y1Value is null/undefined - skip adding to path but still track position
      const y1IsNull = each.y1Value === null || each.y1Value === undefined
      const y2IsNull = each.y2Value === null || each.y2Value === undefined

      const position = {
        x: xPos,
        y1: y1IsNull ? height : yScaleEach(each.y1Value),
        y2: y2IsNull ? height : yScaleEach(each.y2Value),
        value: each.y1Value ?? 0,
        monthIndex: i,
        date: dayjs(each.xValue).format('YYYY-MM-DD')
      }
      positions.push(position)

      // Handle yellow path (y1Value)
      if (!y1IsNull) {
        const yellowCommand = yellowNeedsMoveTo ? 'M' : 'L'
        yellowPathStr += `${yellowCommand}${position.x} ${position.y1}`
        yellowNeedsMoveTo = false
      } else {
        // Null value - next valid point needs MoveTo to start new segment
        yellowNeedsMoveTo = true
      }

      // Handle blue path (y2Value)
      if (!y2IsNull) {
        const blueCommand = blueNeedsMoveTo ? 'M' : 'L'
        bluePathStr += `${blueCommand}${position.x} ${position.y2}`
        blueNeedsMoveTo = false
      } else {
        // Null value - next valid point needs MoveTo to start new segment
        blueNeedsMoveTo = true
      }
    })

    if (!firstSource.values || !firstSource.values.length) {
      yellowPathStr = ''
    }
    if (!secondSource.values || !secondSource.values.length) {
      bluePathStr = ''
    }
  }

  return [yellowPathStr, bluePathStr, positions]
}

export const findNearestXIndex = (array: any, targetX: any) => {
  let minDiff = Infinity
  let nearestObject = null

  for (let i = 0; i < array.length; i++) {
    const diff = Math.abs(array[i].x - targetX)
    if (diff < minDiff) {
      minDiff = diff
      nearestObject = {
        x: array[i].x,
        y1: array[i].y1,
        y2: array[i].y2,
        value: array[i].value,
        index: i,
        monthIndex: array[i].monthIndex
      }
    }
  }

  return nearestObject
}

export function generateDatesArray(startDate: Date, endDate: Date): DummyObjectAca[] {
  const datesArray: DummyObjectAca[] = []
  const currentDate: Date = new Date(startDate)

  // eslint-disable-next-line no-unmodified-loop-condition
  while (currentDate <= endDate) {
    datesArray.push({
      xValue: new Date(currentDate).toISOString(),
      yValue: 0,
      dummy: true
    })
    currentDate.setDate(currentDate.getDate() + 1)
  }

  return datesArray
}

export const formatXLabelCoverage = ({
  date,
  selectedTimePeriod,
  selectedFrequency,
  filteredData,
  dataStartsAt
}: {
  date: string
  selectedTimePeriod: CPITimePeriod
  selectedFrequency: CPIFrequency
  filteredData: any[]
  dataStartsAt: string
}) => {
  if (selectedTimePeriod === CPITimePeriod.Week) {
    return dayjs(date).format('MMM D')
  }
  if (selectedTimePeriod === CPITimePeriod.Month) {
    const d = filterDateByWeek(filteredData, date)
    if (d.length > 0) {
      const dataStartTime = new Date(dataStartsAt).getTime()
      const currentStartTime = new Date(d[0].xValue).getTime()
      const utcDateStart = getUtcDateAndTime(d[0].xValue)
      const utcDateEnd = getUtcDateAndTime(d[d.length - 1].xValue)
      const rangeFrom = utcDateStart.labelFormatDate
      const rangeTo = utcDateEnd.labelFormatDate
      const rangeFromMonth = utcDateStart.month
      const rangeFromYear = utcDateStart.year
      const rangeToMonth = utcDateEnd.month
      const rangeToYear = utcDateEnd.year
      const isSameYear = rangeFromYear === rangeToYear
      const isSameMonth = rangeFromMonth === rangeToMonth
      let rangeFromFormatted = dayjs(rangeFrom).format(`MMM D${!isSameYear ? ', YYYY' : ''}`)
      let rangeToFormatted = dayjs(rangeTo).format(`${!isSameMonth ? 'MMM ' : ''}D${!isSameYear ? ', YYYY' : ''}`)
      const rangeToFormat = rangeFromMonth === rangeToMonth ? 'D' : 'MMM D'
      rangeFromFormatted = dayjs(rangeFrom).format('MMM D')
      rangeToFormatted = dayjs(rangeTo).format(rangeToFormat)
      const todayTime = getTodayTimeUTC()
      const endDayTime = new Date(rangeTo).getTime()

      if (currentStartTime < dataStartTime && selectedFrequency === CPIFrequency.Daily) {
        rangeFromFormatted = dayjs(dataStartsAt).format('MMM D')
      }

      if (todayTime < endDayTime) {
        rangeToFormatted = dayjs(todayTime).format(rangeToFormat)
      }
      if (rangeFrom === rangeTo) {
        rangeToFormatted = ''
      }
      const today = new Date()
      const todayDate = today.getUTCDate()
      const todayMonth = today.getUTCMonth() + 1
      const todayYear = today.getUTCFullYear()
      if (utcDateStart.day === todayDate && todayMonth === utcDateStart.month && todayYear === utcDateStart.year) {
        rangeToFormatted = ''
      }

      if (!rangeToFormatted) return rangeFromFormatted
      return `${rangeFromFormatted}-${rangeToFormatted}`
    }

    return dayjs(date).format('MMM DD')
  }
  if (selectedTimePeriod === CPITimePeriod.ThreeMonths || selectedTimePeriod === CPITimePeriod.SixMonths) {
    if (selectedFrequency === CPIFrequency.Weekly) {
      const groupedDays = filterDateByMonth(filteredData, date)

      if (groupedDays.length < 1) return dayjs(date).format('MMM DD')

      const { rangeFromFormatted, rangeToFormatted } = getRangeFromToFormatted(
        selectedFrequency,
        groupedDays[0].xValue,
        (groupedDays as any)[0].start,
        (groupedDays as any)[groupedDays.length - 1].end
      )

      return `${rangeFromFormatted}-${rangeToFormatted}`
    }

    const currentDate = convertDateToUTCString(new Date(date))

    /*
     * This used to be range from-to, but changed because:
     * ONY-2585 */
    return dayjs(currentDate).format(`MMM 'YY`)
  }
  if (selectedTimePeriod === CPITimePeriod.PastYear) {
    return dayjs(date).format("MMM 'YY")
  }

  return ''
}

export const fillBlankDataCoverage = (
  values: AnalyzerDataValue[],
  selectedTimePeriod: CPITimePeriod
): AnalyzerDataValueFilled[] => {
  const [initialValue, finalValue] = [values[0], values.slice(-1)[0]]
  const date = new Date(initialValue.xValue)
  date.setDate(date.getDate() - 1)
  const subtractedDate = date
  const [initialMonth, finalMonth] = [new Date(subtractedDate).getMonth(), new Date(finalValue.xValue).getMonth()]
  const fMonth = finalMonth === 0 ? 12 : finalMonth
  const selectedMonths = parseInt(getSelectedPeriodNumber(selectedTimePeriod))
  const monthDiff = initialMonth === finalMonth ? selectedMonths : fMonth - initialMonth
  let dummyArray: DummyObjectAca[] = []

  if (selectedMonths >= monthDiff) {
    const fakeMonthsGap = selectedMonths === monthDiff ? selectedMonths - 1 : selectedMonths - monthDiff
    const initDate = new Date(initialValue.xValue)

    if (selectedTimePeriod === CPITimePeriod.Month) {
      const full = 35
      const existingStripes = Math.ceil(values.length / 7)
      const stripesGap = 5 - existingStripes

      if (stripesGap <= 0) return values
      const startValue = values[0].xValue
      const length = values.length
      const remainder = full - length
      const start = dayjs(startValue).subtract(remainder, 'days')
      const formattedStart = dayjs(start).format('YYYY-MM-DD')
      const formattedEnd = dayjs(subtractedDate).format('YYYY-MM-DD')
      const result = generateStringDatesArray(formattedStart, formattedEnd)
      const res = result.map((n) => ({ ...n, xValue: n.xValue.slice(0, 10) }))

      return [...res, ...values].sort((a, b) => (new Date(b.xValue).getTime() >= new Date(a.xValue).getTime() ? -1 : 1))
    } else {
      const dataUntilMonths = initDate.getMonth() - fakeMonthsGap
      const startDate: Date = new Date(initDate.getFullYear(), dataUntilMonths, new Date().getDate())
      const endDate: Date = new Date(subtractedDate)
      dummyArray = generateDatesArray(startDate, endDate)
    }
  }
  ;(values as any) = [...dummyArray, ...values].map((n) => ({
    ...n,
    xValue: dayjs(n.xValue).format(dateFormat.customLabel)
  }))

  return values
}

export const downloadFile = (data: string, fileName: string) => {
  const url = window.URL.createObjectURL(new Blob([data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
  link.remove()
}

export const getInstanceNamesForCsv = (instances: IntegrationInstance[]) => {
  const firstNameParts = instances[0].name.split('_')
  const firstName = firstNameParts.map((c) => c.charAt(0).toUpperCase() + c.slice(1)).join('')
  const secondNameParts = instances[1].name.split('_')
  const secondName = secondNameParts.map((c) => c.charAt(0).toUpperCase() + c.slice(1)).join('')

  return [firstName, secondName]
}

export const getDownloadCsvProps = (instances: IntegrationInstance[]) => {
  const [firstName, secondName] = getInstanceNamesForCsv(instances)

  return `OnyxiaACA_${firstName}_${secondName}_${dayjs().format('YYYY_MM_DD_HH_mm_ss')}.csv`
}

const compareMacAddresses = (
  first: string | string[] | undefined | null,
  second: string | string[] | undefined | null
): boolean => {
  // Both null/undefined
  if (!first && !second) return true
  // One is null/undefined
  if (!first || !second) return false

  const firstArray = Array.isArray(first) ? first : [first]
  const secondArray = Array.isArray(second) ? second : [second]

  // Different lengths means not the same
  if (firstArray.length !== secondArray.length) return false

  // Sort and compare
  const sortedFirst = [...firstArray].sort()
  const sortedSecond = [...secondArray].sort()

  return sortedFirst.every((mac, index) => mac === sortedSecond[index])
}

/*
 * Tooltip Helpers
 */
export const getAssetText = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return 'No data'
  return `${value} asset${value === 1 ? '' : 's'} covered`
}

export const getAssetSubtext = (value: number | null | undefined): string => {
  const text = getAssetText(value)
  const isNoData = value === null || value === undefined

  // For "No data", use simple inline text
  if (isNoData) {
    return `<p class="tooltip-performance subtext">${text}</p>`
  }

  // For data values, always use wrapper that right-aligns when wrapped
  // The wrapper uses flex: 1 1 auto so it shares line when possible, but right-aligns when wrapped
  return `
    <div class="value-wrapper">
      <p class="tooltip-performance subtext">${text}</p>
    </div>
  `
}

export const getSourceTooltipHtml = (
  source: AnalyzerChartData,
  value: number | null | undefined,
  color: string
): string => {
  if (!source.values || source.values.length === 0) return ''

  const assetSubtext = getAssetSubtext(value)

  return `
    <div class='asset-wrapper-coverage'>
      <div class="asset-wrapper-inner">
        <svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'>
          <circle cx='6' cy='6' r='6' fill='${color}' />
        </svg>
        <p class='tooltip-performance'>${source.integrationName}:</p>
      </div>
      ${assetSubtext}
    </div>
  `
}

export const mapAnalysisData = (
  isV2: boolean,
  data: AnalysisData[] | AnalysisDataV2[],
  firstInstanceName: string,
  secondInstanceName: string
): any[] => {
  if (isV2) {
    return data.map((n) => {
      if (n.covered_by === 'both') {
        const firstSourceData = n[firstInstanceName]
        const secondSourceData = n[secondInstanceName]
        const sameHostName = firstSourceData?.host_name === secondSourceData?.host_name
        const sameMacAddress = compareMacAddresses(firstSourceData?.mac_addresses, secondSourceData?.mac_addresses)
        const sameOperatingSystem = firstSourceData?.operating_system === secondSourceData?.operating_system
        const sameSerialNumber = firstSourceData?.serial_number === secondSourceData?.serial_number
        const sameAssetId = firstSourceData?.asset_id === secondSourceData?.asset_id

        return {
          coverage: n.covered_by,
          assetId: firstSourceData?.asset_id || '',
          sameAssetId,
          hostName: firstSourceData?.host_name,
          serialNumber: firstSourceData?.serial_number,
          macAddress: firstSourceData?.mac_addresses,
          lastSeen: firstSourceData?.last_seen,
          operatingSystem: firstSourceData?.operating_system,
          secondAssetId: secondSourceData?.asset_id,
          sameHostName,
          secondHostName: secondSourceData?.host_name,
          sameMacAddress,
          secondMacAddress: secondSourceData?.mac_addresses,
          sameOperatingSystem,
          secondOperatingSystem: secondSourceData?.operating_system,
          sameSerialNumber,
          secondSerialNumber: secondSourceData?.serial_number,
          secondLastSeen: secondSourceData?.last_seen
        }
      }

      return {
        coverage: n.covered_by,
        assetId: n[n.covered_by]?.asset_id || '',
        hostName: n[n.covered_by]?.host_name || '',
        serialNumber: n[n.covered_by]?.serial_number || '',
        macAddress: n[n.covered_by]?.mac_addresses || null,
        lastSeen: n[n.covered_by]?.last_seen || '',
        operatingSystem: n[n.covered_by]?.operating_system || ''
      }
    })
  }

  return data.map((n) => {
    if (n.covered_by === 'both') {
      const firstSourceData = n[firstInstanceName]
      const secondSourceData = n[secondInstanceName]
      const sameHostName = firstSourceData?.host_name === secondSourceData?.host_name
      const sameMacAddress = compareMacAddresses(firstSourceData?.mac_address, secondSourceData?.mac_address)
      const sameOperatingSystem = firstSourceData?.operating_system === secondSourceData?.operating_system
      const sameSerialNumber = firstSourceData?.serial_number === secondSourceData?.serial_number
      const sameAssetId = firstSourceData?.asset_id === secondSourceData?.asset_id

      return {
        coverage: n.covered_by,
        assetId: firstSourceData?.asset_id || '',
        sameAssetId,
        hostName: firstSourceData?.host_name,
        serialNumber: firstSourceData?.serial_number,
        macAddress: firstSourceData?.mac_address,
        lastSeen: firstSourceData?.last_seen,
        operatingSystem: firstSourceData?.operating_system,
        secondAssetId: secondSourceData?.asset_id,
        sameHostName,
        secondHostName: secondSourceData?.host_name,
        sameMacAddress,
        secondMacAddress: secondSourceData?.mac_address,
        sameOperatingSystem,
        secondOperatingSystem: secondSourceData?.operating_system,
        sameSerialNumber,
        secondSerialNumber: secondSourceData?.serial_number,
        secondLastSeen: secondSourceData?.last_seen
      }
    }

    return {
      coverage: n.covered_by,
      assetId: n.asset_id || '',
      hostName: n.host_name,
      serialNumber: n.serial_number,
      macAddress: n.mac_address,
      lastSeen: n.last_seen,
      operatingSystem: n.operating_system
    }
  })
}

/*
 * Maps the Compare-to-All clustering response into the table-row shape. Column values come from the
 * cluster's `canonical` record; `coverage` carries the bucket and the clustering fields are attached
 * for the Coverage column (covering_instances) and the asset-details pane (matching_records_*).
 * See docs/aca-api-spec.md.
 */
/*
 * Resolves the active "Covered by" filter to the server's coverage_comparison for Compare-to-All.
 * The server is fixed (A = picked product, B = all others), but the user may put "All" in Source A,
 * which swaps the UI's A/B relative to the server — sentinelInSourceA handles that swap.
 *
 * All four Venn buckets stay distinct and meaningful even though "All" now contains the picked
 * source (ONYXIA-8188). With the picked source = A and B = All (A ⊆ B):
 *   - Only A (first_not_second)       = empty by construction (you can't be in A but not in All)
 *   - Only B (second_not_first)       = the All-exclusive side (clusters with no picked-source record)
 *   - A and B (intersection)          = clusters that have a picked-source record
 *   - Only A or Only B (sym. diff)    = everything outside the intersection
 * Map each chip to its natural bucket (Only A → first_not_second, etc.); the empty Only A result is
 * the correct, expected behavior — not a bug to remap away.
 */
export const getCompareToAllCoverage = (
  filters: boolean[],
  sentinelInSourceA: boolean
): AcaCoverageFilter | undefined => {
  const index = filters.findIndex((c) => c)
  switch (index) {
    case 0: // "Only A"
      return sentinelInSourceA ? AcaCoverageFilter.SecondNotFirst : AcaCoverageFilter.FirstNotSecond
    case 1: // "Only B"
      return sentinelInSourceA ? AcaCoverageFilter.FirstNotSecond : AcaCoverageFilter.SecondNotFirst
    case 2: // "A and B"
      return AcaCoverageFilter.Intersection
    case 3: // "Only A or Only B"
      return AcaCoverageFilter.SymmetricDifference
    default:
      return undefined
  }
}

export const mapClusterData = (clusters: ClusterRecord[]): AnalysisDataTable[] => {
  return clusters.map((cluster) => {
    /* The records endpoint currently returns cluster-level keys in UPPER_SNAKE (COVERAGE_BUCKET,
     * CANONICAL, MATCHING_RECORDS_A/B, COVERING_INSTANCES) while docs/api-spec2.md documents
     * lower_snake. Read either casing so the table renders whether or not the backend aligns later.
     * The nested record fields (asset_id, hostname, …) are already lowercase as documented. */
    const c = cluster as Record<string, any>
    const canonical: MatchingRecord = c.canonical ?? c.CANONICAL ?? ({} as MatchingRecord)
    const coverageBucket = c.coverage_bucket ?? c.COVERAGE_BUCKET

    return {
      coverage: coverageBucket,
      coverageBucket,
      coveringInstances: c.covering_instances ?? c.COVERING_INSTANCES ?? [],
      matchingRecordsA: c.matching_records_a ?? c.MATCHING_RECORDS_A ?? [],
      matchingRecordsB: c.matching_records_b ?? c.MATCHING_RECORDS_B ?? [],
      assetId: canonical.asset_id || '',
      hostName: canonical.hostname || '',
      serialNumber: canonical.serial_number || '',
      macAddress: canonical.mac_addresses as unknown as string,
      lastSeen: canonical.last_seen || '',
      operatingSystem: canonical.operating_system || ''
    }
  })
}
